import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { css } from '@emotion/react'
import { memo } from 'react'

import Link from '@/components/common/Link'
import { mediaUp, mediaDown } from '@/styles/media'
import { colors, weights } from '@/styles/theme'
import { Text2Styles } from '@/styles/typography'
import { focusVisible, underlineHover } from '@/styles/mixins'
import { usePandfTowns } from '@/service/apiHooks/parkingQueries'
import { AirportResponse } from '@/types/api/town-and-airports'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'
import { useMarket } from '@/utils/multi-markets/context'

import { getArrowIcon } from '@/constants/arrowAssets'

import Submenu from './Submenu'

interface Props {
  click: boolean
  hover: boolean
}

function AirportsSubmenu(props: Props) {
  const { t } = useTranslation('common')
  const { hover = false, click = false } = props
  const { push } = useRouter()
  const { trackClick } = useNavigationTracking()
  const { locale, market } = useMarket()
  const region = market
  const { data: airportsData } = usePandfTowns(locale, region)
  const airports = airportsData?.airports || []
  const handleNavigation = async (route: any) => {
    try {
      await push(route)
    } catch (e) {
      console.error(e)
    }
  }

  const handleAirportClick = (airport: AirportResponse): void => {
    const route = {
      pathname: '/parking-search',
      query: {
        lat: airport.lat,
        lon: airport.lon,
        locationLabel: `${airport.code} - ${airport.cityName}`,
        radius: 50000,
        service: 'PARK_AND_FLY',
        category: airport.cityUrlSlug
      }
    }
    trackClick(`menu_desktop_search_airport_${airport.code}`)
    handleNavigation(route)
  }

  const getAirportHref = (airport: AirportResponse): string => {
    const params = new URLSearchParams({
      lat: airport.lat.toString(),
      lon: airport.lon.toString(),
      locationLabel: `${airport.code} - ${airport.cityName}`,
      radius: '50000',
      service: 'PARK_AND_FLY',
      category: airport.cityUrlSlug
    })
    return `/parking-search?${params.toString()}`
  }

  return (
    <Submenu
      mainLinkContent={
        <Link
          href={{
            pathname: '/parking-search',
            query: {
              service: 'PARK_AND_FLY',
              infoTag: 'PARK_AND_FLY'
            }
          }}
          passHref
          prefetch={false}
        >
          <a
            onClick={() => trackClick('menu_desktop_search_airports_all')}
            css={[mainLinkItem]}
            aria-label={t(
              'navigation.showAllAirportsAccessible' as keyof typeof t
            )}
          >
            {t('navigation.showAllAirports')}
            <img
              src={getArrowIcon('arrow')}
              alt="Arrow icon"
              aria-hidden="true"
            />
          </a>
        </Link>
      }
      linksContent={
        <ul css={content} role="menu">
          {airports
            ?.sort((airport, nextAirport) =>
              airport?.code?.localeCompare(nextAirport.code)
            )
            .map((airport, index) => (
              <li css={link} key={`airport-${airport.code}`} role="menuitem">
                <a
                  href={getAirportHref(airport)}
                  onClick={e => {
                    e.preventDefault()
                    handleAirportClick(airport)
                  }}
                  css={[linkItem]}
                  aria-label={t(
                    'navigation.searchAtAirport' as keyof typeof t,
                    {
                      code: airport.code,
                      city: airport.cityName
                    }
                  )}
                >
                  <span css={airportCode}>{airport.code}</span>
                  {t(airport.name as any)}
                </a>
              </li>
            ))}
        </ul>
      }
      click={click}
      hover={hover}
    />
  )
}

const mainLinkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  margin-top: 18px;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    margin-inline-start: 4px;

    ${mediaDown('lg')} {
      width: 9px;
      height: 9px;
    }

    html[lang^='ar'] & {
      transform: rotate(180deg);
    }
  }

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    margin-top: 0;
  }
`

const content = css`
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 5px;

  ${mediaUp('lg')} {
    padding: 20px;
    padding-top: 0;
    align-items: flex-start;
  }
`

const link = css`
  display: flex;
  margin-top: 12px;

  ${mediaUp('lg')} {
    margin-top: 20px;
  }
`

const linkItem = css`
  ${focusVisible};
  ${Text2Styles};
  ${underlineHover};
  font-weight: ${weights.black};
  color: ${colors.white};
  line-height: normal;
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;

  ${mediaDown('lg')} {
    font-size: 12px;
  }

  ${mediaUp('lg')} {
    color: ${colors.twilightBlue};
    line-height: normal;
  }
`

const airportCode = css`
  font-weight: ${weights.black};
  margin-right: 20px;
  min-width: 50px;
  display: inline-block;
`

export default memo(AirportsSubmenu)
